/**
 * YADA Site Metadata — single source of truth for SEO constants.
 *
 * Anything that ends up in <head> (title suffix, OG image, hreflang, JSON-LD
 * @id) lives here. Per-route translatable copy lives in src/i18n/*.json
 * under the `seo` namespace.
 *
 * `SITE_URL` is the canonical host — used for `og:url`, `<link rel="canonical">`,
 * and JSON-LD `@id`. The test/staging host (yada.edupro.hk) is NOT part of
 * this constant; canonicalisation is handled by the `<SEO>` component (it
 * always emits the canonical to SITE_URL). If you want to canonicalise all
 * traffic to www.yada.hk, add a 301 redirect in nginx — see deploy/README.md.
 */

import { buildLocalePath } from './localePath'
import { SITE } from '../content/site'

/** Canonical origin — keep this in sync with the primary production hostname. */
export const SITE_URL = 'https://www.yada.hk' as const

/** Brand name appended to every <title>. Keep ≤ 30 chars for SERP display. */
export const SITE_NAME = '青年耀進發展協會 YADA' as const

/**
 * Legal name (used in schema.org Organization `name` and footer copyright).
 * Distinct from SITE_NAME which is the marketing brand.
 */
export const LEGAL_NAME = '青年耀進發展協會有限公司' as const

/** English legal name. */
export const LEGAL_NAME_EN = 'Youth Advancement Development Association Limited' as const

/** Fallback OG image. Per-page override via <SEO ogImage=...> takes precedence. */
export const DEFAULT_OG_IMAGE = '/og-image.png' as const

/**
 * Multi-size OG image variants emitted as separate `<meta property="og:image">`
 * tags so social crawlers can pick the best fit. Order = preference (largest first).
 * Add the file to `public/` before referencing it; see `scripts/build-og-images.ts`.
 */
export const OG_IMAGES = [
  { url: '/og-image-1200x630.webp', width: 1200, height: 630, type: 'image/webp' },
  { url: '/og-image-1200x630.png', width: 1200, height: 630, type: 'image/png' },
] as const

/** Locales the site ships with. Order matters: index 0 is the default. */
export const LOCALES = ['zh-Hant', 'zh-Hans', 'en'] as const

export type Locale = (typeof LOCALES)[number]

/** x-default hreflang target — usually the default-locale homepage. */
export const DEFAULT_LOCALE: Locale = 'zh-Hant'

/**
 * Maps an i18next language code to a hreflang value.
 * i18next uses BCP-47 codes already (zh-Hant, zh-Hans, en) so this is an
 * identity map today, but the indirection lets us swap encoding without
 * touching every call site if we ever add e.g. en-GB or zh-Hant-HK.
 */
export const LOCALE_HREFLANG: Record<Locale, string> = {
  'zh-Hant': 'zh-Hant',
  'zh-Hans': 'zh-Hans',
  en: 'en',
}

/**
 * YADA's official social profile URLs — the JSON-LD Organization `sameAs`.
 * Empty array is fine (Google just won't surface social links); add a URL
 * once the profile is confirmed, and don't link to personal accounts.
 *
 * The Facebook fan page is the one confirmed profile (2026-10-29). It is
 * read from `SITE.social` so the URL is defined once — a second copy here
 * would be free to drift from the link the visitor actually clicks.
 *
 * ORGANISATION profiles only. The founder's Person node (`buildPersonForFounder`)
 * accepts a `sameAs` input but no caller passes one, and it should stay that
 * way: a fan page identifies YADA the organisation, not 魏遠強 the person.
 *
 * Not to be confused with `PARTNER_MEDIA_URLS` below: those are editorial
 * *partners* we link TO. Our own profiles belong here.
 */
export const YADA_SAME_AS: readonly string[] = [SITE.social.facebook.link]

/**
 * YADA's Twitter/X handle — used in `<meta name="twitter:site">`. Leave empty
 * to skip the tag (Twitter requires the @ prefix, so we prepend it).
 */
export const TWITTER_SITE_HANDLE = '' // e.g. '@yadahk'

/**
 * YADA's primary keywords — used to seed `meta name="keywords"` on the
 * homepage and as a fallback for the per-page keywords. Google ignores
 * keywords but Baidu still partially uses them, so this helps zh-Hans SEO.
 */
export const DEFAULT_KEYWORDS =
  '青年耀進發展協會,YADA,Ken Ngai,魏遠強,教育傳媒,品格教育,生涯規劃,家長講座,青年發展,香港,My Harbour,ValuesIncubator,AI 升學,AI 微學習'

/**
 * Partner / media outlet URLs. Display name + description are localised
 * in src/i18n/*.json under `common.partners`; this constant only owns the
 * hrefs so they can be checked in one place.
 *
 * Cross-linking rule: every link here must be a real editorial partner
 * (not a paid link exchange or a directory submission). Google explicitly
 * flags "cross-linking between sites you own to manipulate PageRank" as
 * a link scheme — see the "Cross-linking" section of Google's spam
 * policies documentation.
 */
export const PARTNER_MEDIA_URLS: Readonly<Record<string, string>> = {
  goodschool: 'https://www.goodschool.hk/',
}

/**
 * Builds the canonical absolute URL for a given path.
 *
 * The trailing slash is the canonical form, not a stripped one. nginx serves
 * these pages from directories via index.html and 301s the bare form to the
 * slashed form, so `https://www.yada.hk/en/about` is not the page — it is a
 * redirect to `https://www.yada.hk/en/about/`. Pointing a canonical at the
 * redirecting form gives every SEO signal on the site a redirect hop and
 * invites Google to pick its own canonical (it was choosing the homepage).
 *
 * Normalising to exactly one slash also means callers do not have to care
 * whether the router handed us `/about` or `/about/`.
 *
 * THE ONE EXCEPTION — A `.html` FILE
 * ---------------------------------
 * A path that ends in `.html` is a FILE, not a directory, so it takes NO
 * trailing slash: `…/national-education/KS2-v3.html` is the URL nginx
 * serves, and `…/KS2-v3.html/` is a 404. Appending the slash would point
 * the canonical — and every hreflang sibling that sits next to it — at a
 * dead URL. `buildAlternateUrl` below has the same exception; the two must
 * agree, or a locale copy canonicalises to one thing while its hreflang
 * cluster points at another.
 */
export function buildCanonicalUrl(path: string): string {
  const normalised = path.startsWith('/') ? path : `/${path}`
  const trimmed = normalised.replace(/\/+$/, '')
  return trimmed.endsWith('.html') ? `${SITE_URL}${trimmed}` : `${SITE_URL}${trimmed}/`
}

/**
 * Builds the absolute URL for a given locale's variant of `path`.
 * Used to emit `<link rel="alternate" hreflang>` in <SEO> and in the
 * generated sitemap.
 *
 * The trailing slash matches `buildCanonicalUrl` — and it must. nginx serves
 * these pages from directories via index.html and 301s the slash-less form to
 * the slashed form, so `https://www.yada.hk/en/about` is not the page, it is
 * a redirect to `https://www.yada.hk/en/about/`. A hreflang sibling that
 * points at the redirecting form hands Google a URL it has to chase, and it
 * stops matching the canonical it sits next to.
 *
 * THE ONE EXCEPTION — A `.html` FILE
 * ---------------------------------
 * A path that ends in `.html` is a FILE, not a directory, so it takes NO
 * trailing slash: `…/national-education/KS2-v3.html` is the URL nginx
 * serves, and `…/KS2-v3.html/` is a 404. Appending the slash would make
 * every hreflang sibling of a file-style page point at a dead URL.
 * Directory-style URLs are unaffected — the output is byte-identical to
 * what it was before this exception existed.
 */
export function buildAlternateUrl(locale: Locale, path: string): string {
  // SEO PR2 Phase 6: emit a locale-prefixed URL (e.g. /zh-Hans/about) so
  // hreflang siblings point at the actual translated URL instead of the
  // same canonical. DEFAULT_LOCALE ('zh-Hant') returns the canonical form
  // (no prefix) — callers don't need to know about the convention.
  const localised = buildLocalePath(path, locale)
  const trimmed = localised.replace(/\/+$/, '')
  return trimmed.endsWith('.html') ? `${SITE_URL}${trimmed}` : `${SITE_URL}${trimmed}/`
}